import type { ReferenceChatAvailabilitiesResponse } from '@/entities/references/api/generated/model';
import { ReferenceChatType } from '@/entities/references/api/generated/model';
import type { SessionStorageKey } from '@/shared/lib/storage';
import { getSessionStorage } from '@/shared/lib/storage';
import { isDebugEnabled } from '@/shared/lib/system';

// テナント・都道府県ごとに利用可否が変わる chatType と、その debug override SessionStorage key の対応表。
// 常時対応の chatType（special_allocation_taxes, national_grants）は載せない（即 false が返る）。
// 新しいテナント制限ありの chatType を追加する場合はここに 1 行追加する。
const TENANT_GATED_TYPES = {
  [ReferenceChatType.prefectural_grants]: 'debug-prefectural-grants-override',
  [ReferenceChatType.prefectural_special_allocation_taxes]:
    'debug-prefectural-special-allocation-taxes-override',
} as const satisfies Partial<Record<ReferenceChatType, SessionStorageKey>>;

/**
 * リファレンスチャットがテナントで利用不可かを判定する
 *
 * 判定の優先順位（高い順）:
 * 1. デバッグ上書き（`isDebugEnabled()` の場合のみ）
 * 2. API レスポンス（availabilities）
 * 3. API 未取得・失敗時は安全側（未対応扱い）
 *
 * 「常時対応」種別（special_allocation_taxes, national_grants）は早期 return で false。
 */
export const isReferenceChatNotSupported = ({
  chatType,
  availabilities,
}: {
  chatType: ReferenceChatType;
  availabilities: ReferenceChatAvailabilitiesResponse | undefined;
}): boolean => {
  const debugKey = TENANT_GATED_TYPES[chatType as keyof typeof TENANT_GATED_TYPES];
  if (!debugKey) return false;

  if (isDebugEnabled()) {
    const override = getSessionStorage(debugKey);
    if (override === 'enabled') return false;
    if (override === 'disabled') return true;
  }

  return availabilities?.[chatType] !== true;
};
